<template>
  <div class="invoice-management-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>开票管理</span>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.invoiceNumber"
        placeholder="发票号码"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      
      <el-date-picker
        v-model="startDate"
        type="date"
        placeholder="查询时间起"
        aria-label="查询时间起"
        value-format="yyyy-MM-dd"
        clearable
        class="search-input date-picker"
      ></el-date-picker>
      <el-date-picker
        v-model="endDate"
        type="date"
        placeholder="查询时间止"
        aria-label="查询时间止"
        value-format="yyyy-MM-dd"
        clearable
        class="search-input date-picker"
      ></el-date-picker>
      
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      
      <!-- 新增开票按钮 -->
      <el-button v-permission="'settlement:invoice-management:create'" type="success" @click="showAddInvoiceDialog" icon="el-icon-plus" style="margin-left: 10px;">新增开票</el-button>
    </div>
    
    <!-- 发票列表 -->
    <el-table
      :data="invoiceList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column
        label="发票号码"
        width="150"
        align="center"
      >
        <template slot-scope="scope">
          {{ scope.row.invoiceNumber || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="开票时间"
        width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatDate(scope.row.invoiceDate) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="开票金额"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatAmount(scope.row.amount) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="关联结算单"
        min-width="200"
        align="center"
      >
        <template slot-scope="scope">
          <div v-if="scope.row.settlements && scope.row.settlements.length > 0">
            <el-tag
              v-for="(settlement, index) in scope.row.settlements"
              :key="index"
              type="info"
              size="small"
              style="margin-right: 5px;"
            >
              {{ settlement.settlementNumber || settlement.settlement_no }}
            </el-tag>
          </div>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column
        label="发票截图"
        width="120"
        align="center"
      >
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.screenshotPath"
            :src="getAttachmentUrl(scope.row.screenshotPath)"
            :preview-src-list="[getAttachmentUrl(scope.row.screenshotPath)]"
            style="width: 60px; height: 60px; cursor: pointer;"
            fit="cover"
          ></el-image>
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column
        label="创建时间"
        width="180"
        align="center"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      
      <el-table-column
        label="操作"
        width="240"
        align="center"
        fixed="right"
      >
        <template slot-scope="scope">
          <el-button
            v-permission="'settlement:invoice-management:print'"
            type="success"
            size="mini"
            icon="el-icon-printer"
            @click="printInvoice(scope.row)"
            :disabled="!scope.row.originalFilePath"
          >打印</el-button>
          <el-button
            v-permission="'settlement:invoice-management:edit'"
            type="primary"
            size="mini"
            @click="editInvoice(scope.row)"
          >编辑</el-button>
          <el-button
            v-permission="'settlement:invoice-management:delete'"
            type="danger"
            size="mini"
            @click="deleteInvoice(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 30, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      ></el-pagination>
    </div>
    
    <!-- 新增/编辑发票对话框 -->
    <el-dialog
      v-dialog-drag
      :title="invoiceDialogTitle"
      :visible.sync="invoiceDialogVisible"
      width="600px"
      top="8px"
      :close-on-click-modal="false"
      @close="resetInvoiceForm"
    >
      <el-form
        :model="invoiceForm"
        :rules="invoiceFormRules"
        ref="invoiceForm"
        label-width="120px"
      >
        <el-form-item label="开票时间" prop="invoiceDate">
          <el-date-picker
            v-model="invoiceForm.invoiceDate"
            type="date"
            placeholder="选择开票时间"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        
        <el-form-item label="发票号码" prop="invoiceNumber">
          <el-input
            v-model="invoiceForm.invoiceNumber"
            placeholder="请输入发票号码"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="开票金额" prop="amount">
          <el-input-number
            v-model="invoiceForm.amount"
            :precision="2"
            :min="0"
            style="width: 100%"
            placeholder="请输入开票金额"
          ></el-input-number>
        </el-form-item>
        
        <el-form-item label="发票截图" prop="screenshotPath">
          <el-upload
            ref="screenshotUpload"
            class="invoice-uploader"
            :http-request="handleScreenshotUpload"
            :before-upload="beforeFileUpload"
            :show-file-list="false"
            accept=".jpg,.jpeg,.png,.pdf"
          >
            <img v-if="screenshotPreview" :src="screenshotPreview" class="invoice-image" />
            <div v-else class="upload-placeholder">
              <i class="el-icon-plus invoice-uploader-icon"></i>
            </div>
            <div slot="tip" class="el-upload__tip">支持jpg/png/pdf文件，且不超过10MB</div>
          </el-upload>
          <div v-if="invoiceForm.screenshotPath" class="file-path">
            <el-tag size="small" closable @close="clearScreenshot">{{ invoiceForm.screenshotPath }}</el-tag>
          </div>
        </el-form-item>
        
        <el-form-item label="发票原件" prop="originalFilePath">
          <el-upload
            ref="originalUpload"
            class="invoice-uploader"
            :http-request="handleOriginalUpload"
            :before-upload="beforeFileUpload"
            :show-file-list="false"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          >
            <div class="upload-placeholder">
              <i class="el-icon-upload invoice-uploader-icon"></i>
            </div>
            <div slot="tip" class="el-upload__tip">支持pdf/doc/docx/jpg/png文件，且不超过10MB</div>
          </el-upload>
          <div v-if="invoiceForm.originalFilePath" class="file-path">
            <el-tag size="small" closable @close="clearOriginal">{{ invoiceForm.originalFilePath }}</el-tag>
          </div>
        </el-form-item>
        
        <el-form-item label="关联结算单" prop="settlementIds">
          <el-select
            v-model="invoiceForm.settlementIds"
            multiple
            filterable
            placeholder="请选择关联的结算单（可多选）"
            style="width: 100%"
          >
            <el-option
              v-for="settlement in availableSettlements"
              :key="settlement.id"
              :label="`${settlement.settlementNumber || settlement.settlement_no} - ${settlement.settlementTitle || settlement.settlement_title}`"
              :value="settlement.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="invoiceDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitInvoiceForm" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { save_invoice, query_invoices_pagination, query_invoice_by_id, delete_invoice } from '@/api/modules/invoice'
import { upload_attachment, download_attachment } from '@/api/modules/attachment'
import { query_confirmed_settlements_by_unit } from '@/api/modules/settlement'
import { getUserInfo } from '@/utils/auth'
import { getApiUrl } from '@/utils/env'
import { installPlatformPrintFooter } from '@/utils/print-footer'

export default {
  name: 'InvoiceManagement',
  data() {
    return {
      loading: false,
      searchParams: {
        invoiceNumber: ''
      },
      startDate: '',
      endDate: '',
      invoiceList: [],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      currentUnitId: null,
      
      // 发票对话框
      invoiceDialogVisible: false,
      invoiceDialogTitle: '新增开票',
      submitLoading: false,
      
      // 发票表单
      invoiceForm: {
        id: null,
        invoiceDate: '',
        invoiceNumber: '',
        amount: null,
        screenshotPath: '',
        originalFilePath: '',
        settlementIds: []
      },
      
      // 文件预览
      screenshotPreview: '',
      originalPreview: '',
      
      // 表单验证规则
      invoiceFormRules: {
        invoiceDate: [
          { required: true, message: '请选择开票时间', trigger: 'change' }
        ],
        invoiceNumber: [
          { required: true, message: '请输入发票号码', trigger: 'blur' }
        ],
        amount: [
          { required: true, message: '请输入开票金额', trigger: 'blur' },
          { type: 'number', min: 0, message: '开票金额必须大于0', trigger: 'blur' }
        ],
        settlementIds: [
          { required: true, type: 'array', min: 1, message: '请至少选择一个结算单', trigger: 'change' }
        ]
      },
      
      // 可选的结算单列表（只包含已确认状态的结算单）
      availableSettlements: []
    }
  },
  created() {
    // 初始化时加载发票列表和结算单列表
    this.initCurrentUnit()
    this.loadInvoiceList()
    this.loadAvailableSettlements()
  },
  methods: {
    // 初始化当前登录用户的单位ID
    initCurrentUnit() {
      try {
        const userInfo = getUserInfo()
        if (userInfo) {
          this.currentUnitId =
            userInfo.unitId ||
            userInfo.unit_id ||
            userInfo.unitInfoId ||
            (userInfo.unit && userInfo.unit.id) ||
            null
        }
        if (!this.currentUnitId) {
          console.warn('未获取到当前用户单位ID，可能影响结算单筛选')
        }
      } catch (error) {
        console.error('初始化当前用户单位ID失败:', error)
        this.currentUnitId = null
      }
    },

    // 加载可用的结算单列表（只包含已确认状态的结算单）
    async loadAvailableSettlements() {
      try {
        if (!this.currentUnitId) {
          this.initCurrentUnit()
        }
        if (!this.currentUnitId) {
          console.warn('无法查询结算单: 缺少当前用户单位ID')
          this.availableSettlements = []
          return
        }
        
        const response = await query_confirmed_settlements_by_unit(this.currentUnitId)
        
        if (response.data && response.data.success) {
          const data = response.data.data || {}
          
          // 处理不同的响应格式
          if (data.content) {
            this.availableSettlements = data.content || []
          } else if (data.list) {
            this.availableSettlements = data.list || []
          } else if (Array.isArray(response.data.data)) {
            this.availableSettlements = response.data.data || []
          } else {
            this.availableSettlements = []
          }
        } else {
          console.error('获取结算单列表失败:', response.data?.message)
          this.availableSettlements = []
        }
      } catch (error) {
        console.error('获取结算单列表请求错误:', error)
        this.availableSettlements = []
      }
    },
    
    // 加载发票列表
    async loadInvoiceList() {
      if (this.startDate && this.endDate && this.startDate > this.endDate) {
        this.$message.warning('查询时间起不能晚于查询时间止')
        return
      }
      this.loading = true
      
      try {
        // 构建查询参数
        const params = {
          page: this.pagination.current - 1, // 转换为0基页码
          size: this.pagination.pageSize,
          sortBy: 'invoiceDate',
          sortDir: 'desc'
        }
        
        // 添加搜索条件
        if (this.searchParams.invoiceNumber) {
          params.invoiceNumber = this.searchParams.invoiceNumber
        }
        
        if (this.startDate) params.startDate = this.startDate
        if (this.endDate) params.endDate = this.endDate
        
        const response = await query_invoices_pagination(params)
        
        // 处理分页响应格式
        if (response.data && response.data.success) {
          const data = response.data.data || {}
          if (data.content) {
            this.invoiceList = data.content || []
            this.pagination.total = data.totalElements || 0
            this.pagination.current = (data.pageNumber || 0) + 1 // 转换为1基页码
          } else if (Array.isArray(response.data.data)) {
            this.invoiceList = response.data.data
            this.pagination.total = response.data.data.length
          } else {
            this.invoiceList = []
            this.pagination.total = 0
          }
        } else {
          console.error('获取发票列表失败:', response.data?.message)
          this.$message.error(response.data?.message || '获取发票列表失败')
          this.invoiceList = []
          this.pagination.total = 0
        }
      } catch (error) {
        console.error('获取发票列表请求错误:', error)
        this.$message.error('获取发票列表失败，请稍后重试')
        this.invoiceList = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.loadInvoiceList()
    },
    
    // 重置搜索
    handleReset() {
      this.searchParams = {
        invoiceNumber: ''
      }
      this.startDate = ''
      this.endDate = ''
      this.pagination.current = 1
      this.loadInvoiceList()
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.current = 1
      this.loadInvoiceList()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.current = val
      this.loadInvoiceList()
    },
    
    // 显示新增发票对话框
    showAddInvoiceDialog() {
      this.invoiceDialogTitle = '新增开票'
      this.invoiceForm.id = null
      this.resetInvoiceForm()
      this.invoiceDialogVisible = true
    },
    
    // 编辑发票
    async editInvoice(row) {
      this.invoiceDialogTitle = '编辑开票'
      
      try {
        // 加载发票详情
        const response = await query_invoice_by_id(row.id)
        
        if (response.data && response.data.success) {
          const invoiceData = response.data.data
          
          this.invoiceForm = {
            id: invoiceData.id,
            invoiceDate: invoiceData.invoiceDate,
            invoiceNumber: invoiceData.invoiceNumber,
            amount: invoiceData.amount,
            screenshotPath: invoiceData.screenshotPath || '',
            originalFilePath: invoiceData.originalFilePath || '',
            settlementIds: invoiceData.settlementIds || (invoiceData.settlements ? invoiceData.settlements.map(s => s.id) : [])
          }
          
          // 如果有截图路径，设置预览（使用附件URL）
          if (invoiceData.screenshotPath) {
            // 表格预览使用URL，表单预览也使用URL
            this.screenshotPreview = this.getAttachmentUrl(invoiceData.screenshotPath)
          }
        } else {
          this.$message.error('加载发票详情失败')
          return
        }
      } catch (error) {
        console.error('加载发票详情失败:', error)
        this.$message.error('加载发票详情失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        return
      }
      
      this.invoiceDialogVisible = true
    },
    
    // 打印发票
    async printInvoice(row) {
      if (!row.originalFilePath) {
        this.$message.warning('该发票没有原件文件，无法打印')
        return
      }
      
      try {
        // 显示加载提示
        const loading = this.$loading({
          lock: true,
          text: '正在加载文件...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 下载文件
        const response = await download_attachment(row.originalFilePath)
        
        // 创建Blob对象
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)
        
        // 判断文件类型
        const contentType = response.headers['content-type'] || ''
        const isPdf = contentType.includes('pdf') || row.originalFilePath.toLowerCase().endsWith('.pdf')
        const isImage = contentType.includes('image') || /\.(jpg|jpeg|png|gif)$/i.test(row.originalFilePath)
        
        // 关闭加载提示
        loading.close()
        
        // 创建打印窗口
        const printWindow = window.open('', '_blank')
        if (!printWindow) {
          this.$message.error('无法打开打印窗口，请检查浏览器弹窗设置')
          return
        }
        
        // 构建打印HTML内容
        const buildPrintHtml = (content, delay = 500) => {
          const scriptOpen = '<script>'
          const scriptContent = 'window.onload=function(){setTimeout(function(){window.print();window.onafterprint=function(){window.close();};},' + delay + ');};'
          const scriptClose = '</' + 'script>'
          return [
            '<!DOCTYPE html>',
            '<html>',
            '<head>',
            '<title>打印发票</title>',
            '<style>body{margin:0;padding:0}</style>',
            '</head>',
            '<body>',
            content,
            scriptOpen + scriptContent + scriptClose,
            '</body>',
            '</html>'
          ].join('')
        }
        
        let printHtml = ''
        
        if (isPdf) {
          // PDF文件：使用embed标签嵌入PDF
          const pdfContent = '<style>body{margin:0;padding:0}embed{width:100%;height:100vh;border:none}</style><embed src="' + blobUrl + '" type="application/pdf" />'
          printHtml = buildPrintHtml(pdfContent, 500)
        } else if (isImage) {
          // 图片文件：显示图片
          const imageContent = '<style>body{margin:0;padding:20px;display:flex;justify-content:center;align-items:center}img{max-width:100%;height:auto}</style><img src="' + blobUrl + '" alt="发票原件" />'
          printHtml = buildPrintHtml(imageContent, 500)
        } else {
          // 其他文件类型：使用iframe
          const iframeContent = '<style>body{margin:0;padding:0}iframe{width:100%;height:100vh;border:none}</style><iframe src="' + blobUrl + '"></iframe>'
          printHtml = buildPrintHtml(iframeContent, 1000)
        }
        
        printWindow.document.write(printHtml)
        await installPlatformPrintFooter(printWindow, null, {
          documentType: 'INVOICE',
          documentKey: row.id
        })
        printWindow.document.close()
        
        // 清理URL对象（在窗口关闭后）
        printWindow.addEventListener('beforeunload', () => {
          window.URL.revokeObjectURL(blobUrl)
        })
        
      } catch (error) {
        console.error('打印发票失败:', error)
        this.$message.error('加载文件失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      }
    },
    
    // 删除发票
    deleteInvoice(row) {
      this.$confirm('确定要删除此发票吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await delete_invoice(row.id)
          
          if (response.data && response.data.success) {
            this.$message.success('删除成功')
            this.loadInvoiceList() // 重新加载列表
          } else {
            this.$message.error(response.data?.message || '删除失败')
          }
        } catch (error) {
          console.error('删除发票失败:', error)
          this.$message.error('删除失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        }
      }).catch(() => {
        // 用户取消操作
      })
    },
    
    // 重置发票表单
    resetInvoiceForm() {
      this.invoiceForm = {
        id: null,
        invoiceDate: '',
        invoiceNumber: '',
        amount: null,
        screenshotPath: '',
        originalFilePath: '',
        settlementIds: []
      }
      this.screenshotPreview = ''
      this.originalPreview = ''
      if (this.$refs.invoiceForm) {
        this.$refs.invoiceForm.resetFields()
      }
      if (this.$refs.screenshotUpload) {
        this.$refs.screenshotUpload.clearFiles()
      }
      if (this.$refs.originalUpload) {
        this.$refs.originalUpload.clearFiles()
      }
    },
    
    // 提交发票表单
    async submitInvoiceForm() {
      // 先进行表单验证
      try {
        await this.$refs.invoiceForm.validate()
      } catch (error) {
        // 验证失败，不执行提交
return false
      }
      
      try {
        this.submitLoading = true
        
        // 构建提交数据（根据实际API需要调整）
        const submitData = {
          invoiceNumber: this.invoiceForm.invoiceNumber,
          invoiceDate: this.invoiceForm.invoiceDate,
          amount: this.invoiceForm.amount,
          settlementIds: this.invoiceForm.settlementIds
        }
        
        // 如果有文件路径，添加到提交数据
        if (this.invoiceForm.screenshotPath) {
          submitData.screenshotPath = this.invoiceForm.screenshotPath
        }
        if (this.invoiceForm.originalFilePath) {
          submitData.originalFilePath = this.invoiceForm.originalFilePath
        }
        
        // 如果是编辑模式，添加ID
        if (this.invoiceForm.id) {
          submitData.id = this.invoiceForm.id
        }
        
        // 调用API保存发票
        const response = await save_invoice(submitData)
        
        if (response.data && response.data.success) {
          this.$message.success(this.invoiceForm.id ? '编辑成功' : '新增成功')
          this.invoiceDialogVisible = false
          this.loadInvoiceList() // 重新加载列表
          this.loadAvailableSettlements() // 重新加载结算单列表（可能状态已变更）
        } else {
          this.$message.error(response.data?.message || '保存失败')
        }
      } catch (error) {
        console.error('保存发票失败:', error)
        this.$message.error('保存失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      } finally {
        this.submitLoading = false
      }
    },
    
    // 文件上传前验证
    beforeFileUpload(file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB')
        return false
      }
      return true
    },
    
    // 上传发票截图
    async handleScreenshotUpload(options) {
      const file = options.file
      
      try {
        // 确定relatedId：编辑模式使用发票ID，新增模式使用结算单ID（如果已选择）
        let relatedId = null
        if (this.invoiceForm.id) {
          // 编辑模式：使用发票ID
          relatedId = this.invoiceForm.id
        } else {
          // 新增模式：如果有结算单ID则使用，否则使用0（临时上传）
          relatedId = (this.invoiceForm.settlementIds && this.invoiceForm.settlementIds.length > 0) 
            ? this.invoiceForm.settlementIds[0] 
            : 0
        }
        
        // 如果是图片，生成预览
        if (file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = (e) => {
            this.screenshotPreview = e.target.result
          }
          reader.readAsDataURL(file)
        }
        
        // 使用通用附件上传API
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', 'invoice')
        formData.append('relatedId', relatedId)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          this.invoiceForm.screenshotPath = response.data.data?.filePath || ''
          this.$message.success('发票截图上传成功')
          options.onSuccess(response.data)
        } else {
          this.$message.error(response.data?.message || '上传失败')
          options.onError(new Error(response.data?.message || '上传失败'))
        }
      } catch (error) {
        console.error('上传发票截图失败:', error)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },
    
    // 上传发票原件
    async handleOriginalUpload(options) {
      const file = options.file
      
      try {
        // 确定relatedId：编辑模式使用发票ID，新增模式使用结算单ID（如果已选择）
        let relatedId = null
        if (this.invoiceForm.id) {
          // 编辑模式：使用发票ID
          relatedId = this.invoiceForm.id
        } else {
          // 新增模式：如果有结算单ID则使用，否则使用0（临时上传）
          relatedId = (this.invoiceForm.settlementIds && this.invoiceForm.settlementIds.length > 0) 
            ? this.invoiceForm.settlementIds[0] 
            : 0
        }
        
        // 使用通用附件上传API
        const formData = new FormData()
        formData.append('file', file)
        formData.append('category', 'invoice')
        formData.append('relatedId', relatedId)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          this.invoiceForm.originalFilePath = response.data.data?.filePath || ''
          this.$message.success('发票原件上传成功')
          options.onSuccess(response.data)
        } else {
          this.$message.error(response.data?.message || '上传失败')
          options.onError(new Error(response.data?.message || '上传失败'))
        }
      } catch (error) {
        console.error('上传发票原件失败:', error)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },
    
    // 清除截图
    clearScreenshot() {
      this.invoiceForm.screenshotPath = ''
      this.screenshotPreview = ''
      if (this.$refs.screenshotUpload) {
        this.$refs.screenshotUpload.clearFiles()
      }
    },
    
    // 清除原件
    clearOriginal() {
      this.invoiceForm.originalFilePath = ''
      this.originalPreview = ''
      if (this.$refs.originalUpload) {
        this.$refs.originalUpload.clearFiles()
      }
    },
    
    // 格式化金额
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD')
    },
    
    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return '-'
      return moment(dateString).format('YYYY-MM-DD HH:mm:ss')
    },
    
    // 获取附件下载URL（用于显示图片）
    getAttachmentUrl(filePath) {
      if (!filePath) return ''
      
      // 如果是完整的URL（http/https开头），直接返回
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        return filePath
      }
      
      // 如果是data URL（base64），直接返回
      if (filePath.startsWith('data:')) {
        return filePath
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download')
      return `${apiUrl}?filePath=${encodeURIComponent(filePath)}`
    },
    
    // 获取预览图片URL（判断是本地预览还是服务器文件）
    getPreviewImageUrl(imageUrl) {
      if (!imageUrl) return ''
      
      // 如果是data URL（本地预览），直接返回
      if (imageUrl.startsWith('data:')) {
        return imageUrl
      }
      
      // 否则使用getAttachmentUrl转换
      return this.getAttachmentUrl(imageUrl)
    }
  }
}
</script>

<style lang="scss" scoped>
.invoice-management-container {
  padding: 20px;
  background-color: #fff;
  
  .page-header {
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }
      
      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }
  }
  
  .search-area {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
    flex-wrap: wrap;
    
    .search-input {
      width: 200px;
    }
    
    .date-picker {
      width: 200px;
    }
  }
  
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
  
  // 发票上传样式
  .invoice-uploader {
    ::v-deep .el-upload {
      border: 1px dashed #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      width: 100px;
      height: 100px;
      
      &:hover {
        border-color: #409EFF;
      }
    }
    
    .upload-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      
      .invoice-uploader-icon {
        font-size: 28px;
        color: #8c939d;
        margin-bottom: 8px;
      }
      
      .upload-text {
        font-size: 12px;
        color: #8c939d;
      }
    }
    
    .invoice-uploader-icon {
      font-size: 28px;
      color: #8c939d;
      width: 100px;
      height: 100px;
      line-height: 100px;
      text-align: center;
    }
    
    .invoice-image {
      width: 100px;
      height: 100px;
      display: block;
      object-fit: cover;
    }
  }
  
  .file-path {
    margin-top: 8px;
    
    .el-tag {
      max-width: 100%;
      word-break: break-all;
    }
  }
  
  .el-upload__tip {
    margin-top: 5px;
    font-size: 12px;
    color: #606266;
  }
}
</style>
